import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { client, type PhotoInfo } from '../lib/api'
import {
  CONNECTION_INTENT_LABEL,
  OUTREACH_LABEL,
  OUTREACH_OPTIONS,
  placeholderGradient,
  type OutreachPreference,
} from '../lib/ui'
import type { PublicProfile } from '../../shared/types'
import { AdCard } from './AdCard'
import { hideAdCardBanner, showAdCardBanner } from '../lib/admob'
import { ProfileSheet } from './ProfileSheet'
import { LIVE_POLL_SLOW_MS, useLiveRefresh } from '../lib/live'
import { MAXIMUS_USER_ID } from '../../shared/empty-room'

type Filters = {
  showOutreach: OutreachPreference[]
  minAge: number
  maxAge: number
  maxDistanceMiles: number
  requireVerified: boolean
}

function nextAdAfter(min: number, max: number) {
  const lo = Math.min(min, max)
  const hi = Math.max(min, max)
  return lo + Math.floor(Math.random() * (hi - lo + 1))
}

function DiscoverFiltersDrawer({
  open,
  filters,
  setFilters,
  onClose,
  onApply,
}: {
  open: boolean
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  onClose: () => void
  onApply: () => void
}) {
  if (!open) return null

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-label="Discovery filters">
        <div className="drawer-head">
          <h2>Filters</h2>
          <button className="button secondary" type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="field">
          <label>Show outreach styles</label>
          <div className="chip-row">
            {OUTREACH_OPTIONS.map((opt) => {
              const active = filters.showOutreach.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`chip ${active ? 'selected' : ''}`}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      showOutreach: active
                        ? f.showOutreach.filter((d) => d !== opt.value)
                        : [...f.showOutreach, opt.value],
                    }))
                  }
                >
                  {OUTREACH_LABEL[opt.value]}
                </button>
              )
            })}
          </div>
        </div>
        <div className="field">
          <label>Age range</label>
          <div className="range-row">
            <input
              className="input"
              type="number"
              min={18}
              max={99}
              value={filters.minAge}
              onChange={(e) => setFilters((f) => ({ ...f, minAge: Number(e.target.value) }))}
            />
            <span>to</span>
            <input
              className="input"
              type="number"
              min={18}
              max={99}
              value={filters.maxAge}
              onChange={(e) => setFilters((f) => ({ ...f, maxAge: Number(e.target.value) }))}
            />
          </div>
        </div>
        <div className="field">
          <label>Max distance (miles)</label>
          <input
            className="input"
            type="number"
            min={5}
            max={5000}
            value={filters.maxDistanceMiles}
            onChange={(e) => setFilters((f) => ({ ...f, maxDistanceMiles: Number(e.target.value) }))}
          />
        </div>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={filters.requireVerified}
            onChange={(e) => setFilters((f) => ({ ...f, requireVerified: e.target.checked }))}
          />
          Only show verified members
        </label>
        <button className="button primary" type="button" onClick={onApply}>
          Apply filters
        </button>
      </div>
    </>
  )
}

export function Discover({
  onOpenChat,
  onMatch,
  notify,
  onUpgrade,
}: {
  onOpenChat: (peer: PublicProfile) => void
  onMatch: (peer: PublicProfile) => void
  notify: (msg: string) => void
  onUpgrade?: () => void
}) {
  const [profiles, setProfiles] = useState<PublicProfile[] | null>(null)
  const [cardPhotos, setCardPhotos] = useState<Array<{ urlPath: string }>>([])
  const [photoIdx, setPhotoIdx] = useState(0)
  const [detailBio, setDetailBio] = useState<string | null>(null)
  const [sheetId, setSheetId] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Filters>({
    showOutreach: [],
    minAge: 18,
    maxAge: 99,
    maxDistanceMiles: 100,
    requireVerified: false,
  })
  const [decisions, setDecisions] = useState(0)
  const [nextAdAt, setNextAdAt] = useState(20)
  const [viewingAd, setViewingAd] = useState(false)
  const [adPayload, setAdPayload] = useState<{
    headline?: string
    body?: string
    cta?: string
    ctaHref?: string
    demo?: boolean
  } | null>(null)
  const [adsEnabled, setAdsEnabled] = useState(true)
  /** Tinder-style: once you Nope/Like someone this session, never soft-refresh them back in. */
  const decidedIds = useRef(new Set<string>())
  const [emptyRoom, setEmptyRoom] = useState<{
    active: boolean
    searchableMembers: number
    joinedThisWeek: number
    completingProfiles: number
    foundingSlotsRemaining: number
    foundingCap: number
    recentUnlocks: number
  } | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)

  useEffect(() => {
    client
      .me()
      .then((res) => {
        const u = res.user as Record<string, unknown> | null
        if (!u) return
        let showOutreach: OutreachPreference[] = []
        try {
          if (u.show_outreach) showOutreach = JSON.parse(String(u.show_outreach)) as OutreachPreference[]
        } catch {
          /* use defaults */
        }
        setFilters({
          showOutreach,
          minAge: Number(u.min_age ?? 18),
          maxAge: Number(u.max_age ?? 99),
          maxDistanceMiles: Number(u.max_distance_miles ?? 100),
          requireVerified: !!u.require_verified,
        })
      })
      .catch(() => undefined)
    client
      .affiliate()
      .then((a) => {
        const code = a.code
        if (code) setInviteLink(`${window.location.origin}/#/signup?ref=${encodeURIComponent(code)}`)
      })
      .catch(() => undefined)
  }, [])

  const load = useCallback(async () => {
    setProfiles(null)
    try {
      const res = await client.feed()
      if (res.emptyRoom) setEmptyRoom(res.emptyRoom)
      const unique: PublicProfile[] = []
      const seen = new Set<string>()
      for (const p of res.profiles) {
        if (seen.has(p.id) || decidedIds.current.has(p.id)) continue
        seen.add(p.id)
        unique.push(p)
      }
      setProfiles(unique)
    } catch (e) {
      notify((e as Error).message)
      setProfiles([])
    }
  }, [notify])

  useEffect(() => {
    void load()
  }, [load])

  const softRefresh = useCallback(async () => {
    try {
      const res = await client.feed()
      if (res.emptyRoom) setEmptyRoom(res.emptyRoom)
      setProfiles((current) => {
        const list = res.profiles
        if (!current) return list.filter((p) => !decidedIds.current.has(p.id))
        if (!current.length) return list.filter((p) => !decidedIds.current.has(p.id))
        const have = new Set(current.map((p) => p.id))
        const incoming = list.filter((p) => !have.has(p.id) && !decidedIds.current.has(p.id))
        if (!incoming.length) return current
        return [...current, ...incoming]
      })
    } catch {
      /* quiet background refresh */
    }
  }, [])

  useLiveRefresh(() => {
    void softRefresh()
  }, LIVE_POLL_SLOW_MS, true)

  useEffect(() => {
    client
      .ads('discover_swipe')
      .then((res) => {
        setAdsEnabled(!!res.ad)
        if (res.ad) setAdPayload(res.ad)
        const min = res.interval?.min ?? 15
        const max = res.interval?.max ?? 25
        setNextAdAt(nextAdAfter(min, max))
      })
      .catch(() => setAdsEnabled(false))
  }, [])

  const profile = profiles && profiles.length > 0 ? profiles[0] : null
  const isTestBot = profile?.lifestyleTags.includes('Demo') || profile?.lifestyleTags.includes('Test bot')

  useEffect(() => {
    setCardPhotos([])
    setPhotoIdx(0)
    setDetailBio(null)
    if (!profile) return
    void client.recordProfileView(profile.id).catch(() => undefined)
    let cancelled = false
    client
      .profileDetail(profile.id)
      .then((detail) => {
        if (cancelled) return
        const pubs = detail.photos.filter((p: PhotoInfo) => p.kind === 'public' && p.urlPath) as Array<{ urlPath: string }>
        setCardPhotos(pubs)
        setDetailBio((detail.profile.bio as string) ?? null)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [profile?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentPhoto = cardPhotos[photoIdx]?.urlPath ?? null

  const recordDecision = () => {
    setDecisions((d) => {
      const next = d + 1
      if (adsEnabled && adPayload && next >= nextAdAt) {
        setViewingAd(true)
        void showAdCardBanner()
        setDecisions(0)
        setNextAdAt(nextAdAfter(15, 25))
      }
      return next
    })
  }

  const dismissAd = () => {
    setViewingAd(false)
    void hideAdCardBanner()
  }

  const like = async () => {
    if (!profile || viewingAd) return
    try {
      const res = await client.like(profile.id)
      decidedIds.current.add(profile.id)
      if (res.matched) onMatch(profile)
      setProfiles((list) => (list ? list.filter((p) => p.id !== profile.id) : list))
      recordDecision()
    } catch (e) {
      notify((e as Error).message)
    }
  }

  const pass = async () => {
    if (!profile || viewingAd) return
    try {
      await client.pass(profile.id)
      decidedIds.current.add(profile.id)
      setProfiles((list) => (list ? list.filter((p) => p.id !== profile.id) : list))
      recordDecision()
    } catch (e) {
      notify((e as Error).message)
    }
  }

  const rewind = async () => {
    try {
      const res = await client.rewindPass()
      decidedIds.current.delete(res.restoredUserId)
      notify('Last skip undone')
      await load()
    } catch (e) {
      notify((e as Error).message)
    }
  }

  const applyFilters = async () => {
    setShowFilters(false)
    try {
      await client.updateProfile({
        showOutreach: filters.showOutreach.length ? filters.showOutreach : undefined,
        minAge: filters.minAge,
        maxAge: filters.maxAge,
        maxDistanceMiles: filters.maxDistanceMiles,
        requireVerified: filters.requireVerified,
      })
      await load()
    } catch (e) {
      notify((e as Error).message)
    }
  }

  let content: ReactNode

  if (profiles === null) {
    content = (
      <div className="loading-center">
        <div className="spinner" />
      </div>
    )
  } else if (!profile) {
    content = (
      <div className="panel-page">
        <span className="eyebrow">You&apos;re early</span>
        <h1>Discover</h1>
        <div className="empty empty-room">
          <div>◎</div>
          <h2>Building the room — honestly</h2>
          <p>
            No fake locals. Pairvelle is still filling in. Finish setup, invite someone interesting, and message Maximus
            in your welcome thread while people arrive.
          </p>
          {emptyRoom && (
            <ul className="empty-room-stats">
              <li>
                <strong>{emptyRoom.joinedThisWeek}</strong> people joined this week
              </li>
              <li>
                <strong>{emptyRoom.completingProfiles}</strong> are currently completing profiles
              </li>
              <li>
                <strong>{emptyRoom.recentUnlocks}</strong> new profiles unlocked in the last day
              </li>
              <li>
                Founding slots left: <strong>{emptyRoom.foundingSlotsRemaining}</strong> / {emptyRoom.foundingCap}
              </li>
            </ul>
          )}
          <div className="onboarding-actions">
            <button
              className="button primary"
              type="button"
              onClick={() =>
                onOpenChat({
                  id: MAXIMUS_USER_ID,
                  displayName: 'Maximus',
                  age: null,
                  city: null,
                  distanceMiles: null,
                  outreachPreference: 'either',
                  connectionIntent: 'exploring',
                  lifestyleTags: ['Founder', 'Welcome'],
                  heading: 'Welcome host',
                  bio: null,
                  accentColor: '#8ed9b7',
                  verified: true,
                  online: true,
                  membershipBadge: 'Founder',
                  welcomeHost: true,
                })
              }
            >
              Message Maximus
            </button>
            {inviteLink && (
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(inviteLink).then(
                    () => notify('Invite link copied — you both get 50 credits after they verify'),
                    () => notify(inviteLink),
                  )
                }}
              >
                Invite someone (+50 / +50)
              </button>
            )}
            <button className="button secondary" type="button" onClick={() => setShowFilters(true)}>
              Adjust filters
            </button>
            <button className="button secondary" type="button" onClick={() => void rewind()}>
              Undo last skip
            </button>
            <button className="button secondary" type="button" onClick={() => void load()}>
              Refresh feed
            </button>
          </div>
        </div>
      </div>
    )
  } else if (viewingAd && adPayload) {
    content = (
      <AdCard
        ad={adPayload}
        onContinue={dismissAd}
        onUpgrade={() => {
          dismissAd()
          onUpgrade?.()
        }}
      />
    )
  } else {
    content = (
      <div className="discover-layout discover-page">
        <div className="discover-heading discover-heading--compact">
          <div className="discover-heading-main">
            <span className="eyebrow discover-eyebrow">Discover</span>
            <h1 className="discover-title-full">People who chose how they connect</h1>
            <h1 className="discover-title-short">Discover</h1>
          </div>
          <div className="discover-heading-actions">
            <span className="free-pill">{adsEnabled ? 'Free · ads' : 'Ad-free'}</span>
            <button className="button secondary discover-filter-btn" type="button" onClick={() => setShowFilters(true)}>
              Filters
            </button>
          </div>
        </div>

        {emptyRoom?.active && (
          <div className="empty-room-banner">
            <strong>You&apos;re early.</strong>{' '}
            {emptyRoom.joinedThisWeek} joined this week · {emptyRoom.completingProfiles} finishing profiles
            {emptyRoom.recentUnlocks > 0 ? ` · ${emptyRoom.recentUnlocks} unlocked in the last day` : ''}. No fake locals.
          </div>
        )}

        <div className="discover-card-stack">
        <article
          className={`profile-card profile-card--tappable profile-card--discover${profile.welcomeHost ? ' profile-card--welcome' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setSheetId(profile.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setSheetId(profile.id)
            }
          }}
        >
          {profile.welcomeHost && <span className="welcome-host-badge">Founder welcome</span>}
          {currentPhoto ? (
            <img alt={`${profile.displayName}'s profile`} src={currentPhoto} />
          ) : (
            <div
              className="profile-photo-fallback"
              style={{ background: placeholderGradient(profile.accentColor) }}
              aria-hidden
            >
              {profile.displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
          {cardPhotos.length > 1 && (
            <div className="profile-photo-dots" onClick={(e) => e.stopPropagation()}>
              {cardPhotos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={i === photoIdx ? 'active' : ''}
                  aria-label={`Photo ${i + 1}`}
                  onClick={() => setPhotoIdx(i)}
                />
              ))}
            </div>
          )}
          {cardPhotos.length > 1 && (
            <div className="profile-photo-nav" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="button secondary"
                aria-label="Previous photo"
                disabled={photoIdx === 0}
                onClick={() => setPhotoIdx((i) => i - 1)}
              >
                ‹
              </button>
              <button
                type="button"
                className="button secondary"
                aria-label="Next photo"
                disabled={photoIdx >= cardPhotos.length - 1}
                onClick={() => setPhotoIdx((i) => i + 1)}
              >
                ›
              </button>
            </div>
          )}
          <div className="image-shade" />
          <div className="profile-top">
            {profile.verified && <span className="verified">✓ Verified</span>}
            {isTestBot && <span className="verified test-bot-badge">Test bot</span>}
            {profile.city && (
              <span className="distance">
                {profile.city}
                {profile.distanceMiles != null ? ` · ${profile.distanceMiles} mi` : ''}
              </span>
            )}
          </div>
          <div className="profile-info">
            <span className="match-pill">
              {OUTREACH_LABEL[profile.outreachPreference]} · {CONNECTION_INTENT_LABEL[profile.connectionIntent]}
            </span>
            <h2>
              {profile.displayName}
              {profile.age != null ? `, ${profile.age}` : ''}
            </h2>
            <p className="profile-card-bio">{detailBio || profile.heading || 'No bio yet.'}</p>
            {profile.lifestyleTags.length > 0 && (
              <div className="detail-pills">
                {profile.lifestyleTags.slice(0, 4).map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        </article>
        </div>

        <div className="profile-actions profile-actions--discover">
          <button aria-label="Skip" className="circle-action skip" type="button" onClick={pass}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
          <button className="message-action" type="button" onClick={() => onOpenChat(profile)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
            </svg>
            Message
          </button>
          <button aria-label="Express interest" className="circle-action like" type="button" onClick={like}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {content}
      <ProfileSheet
        profileId={sheetId}
        onClose={() => setSheetId(null)}
        notify={notify}
        variant="fullscreen"
        showActions
        onPass={async (id) => {
          setSheetId(null)
          try {
            await client.pass(id)
            decidedIds.current.add(id)
            setProfiles((list) => (list ? list.filter((p) => p.id !== id) : list))
            recordDecision()
          } catch (e) {
            notify((e as Error).message)
          }
        }}
        onLike={async (p) => {
          setSheetId(null)
          try {
            const res = await client.like(p.id)
            decidedIds.current.add(p.id)
            if (res.matched) onMatch(p)
            setProfiles((list) => (list ? list.filter((x) => x.id !== p.id) : list))
            recordDecision()
          } catch (e) {
            notify((e as Error).message)
          }
        }}
        onMessage={(p) => {
          setSheetId(null)
          onOpenChat(p)
        }}
      />
      <DiscoverFiltersDrawer
        open={showFilters}
        filters={filters}
        setFilters={setFilters}
        onClose={() => setShowFilters(false)}
        onApply={applyFilters}
      />
    </>
  )
}
